/**
 * Shared slugify/uniquify primitives for deriving an identifier from a display name.
 *
 * Used by both `features/workflows/builder/identifiers.ts` (step/phase ids) and
 * `features/llm-profiles/slug.ts` (profile slugs) — same lowercasing/hyphen-collapsing
 * rule and the same "-2, -3, …" uniquification, previously duplicated between the two.
 */

/**
 * Lowercases `text`, collapses runs of non-alphanumeric characters to a single hyphen,
 * and trims leading/trailing hyphens. Falls back to `fallback` for input that slugifies
 * to nothing (e.g. all-punctuation or non-Latin) — a derived id is required and `""` is
 * not one.
 */
export function slugify(text: string, fallback: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

function truncate(value: string, maxLength: number): string {
  if (!Number.isFinite(maxLength) || value.length <= maxLength) return value;
  return value.slice(0, Math.max(1, maxLength)).replace(/-+$/, '');
}

/**
 * Appends the smallest `-2`, `-3`, … suffix that makes `base` unique against `taken`,
 * re-truncating so a suffixed result still respects `maxLength`. Starts at 2 rather
 * than 1 because the first duplicate of `specify` reads as "the second specify", and
 * `specify-1` implies a `specify-0` that never exists.
 */
export function uniquify(
  base: string,
  taken: Iterable<string>,
  maxLength: number = Infinity,
): string {
  const used = new Set(taken);
  const truncatedBase = truncate(base, maxLength);
  if (!used.has(truncatedBase)) return truncatedBase;

  for (let suffixNumber = 2; ; suffixNumber += 1) {
    const suffix = `-${String(suffixNumber)}`;
    const candidate =
      truncate(truncatedBase, maxLength - suffix.length) + suffix;
    if (!used.has(candidate)) return candidate;
  }
}
